// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import Button from "@pacific/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { color, ellipsis, mergeWithCss, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    buttonLabel?: string;
    title: string;
    description?: string;
    buttonLink?: LinkFieldValue;
    imageUrl: string;
    openInNewTab?: boolean;
    elementId?: string;
    extendedStyles?: InformationCardStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    session: getCustomTypeSession(state),
    currentBillTo: getCurrentBillToState(state).value,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface InformationCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    dataWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
}

export const espotCardStyles: InformationCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 248px;
            height: 248px;
            flex: 1 0 auto;
            box-shadow: 0 -4px 0 0 ${color("secondary.main")};
            margin-right: 32px;
            ${xScreen("lte", "md")} {
                width: 256px;
                height: 256px;
                margin-right: 14px;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
                height: auto;
                margin-bottom: 32px;
                margin-right: 0;
            }
        `,
    },
    image: {
        height: "248px",
        transformations: {
            width: 248,
            tabletWidth: 256,
            mobileWidth: 343,
        },
        css: css`
            width: 100%;
            max-height: 100%;
            justify-content: flex-start;
        `,
    },
    dataWrapper: {
        css: css`
            padding-top: 36px;
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "sm")} {
                padding-top: 0;
            }
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin-bottom: 8px;
        `,
    },
    description: {
        variant: "p",
        css: css`
            margin-bottom: 11px;
            ${ellipsis(4)}
            ${TypographyStyle} {
                margin-bottom: 0;
            }
            ${xScreen("lte", "md")} {
                margin-bottom: 20px;
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 12px;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-grow: 1;
            align-items: flex-end;
            ${xScreen("lte", "md")} {
                flex-grow: 0;
            }
        `,
    },
};

const EspotCard: React.FC<Props> = ({
    buttonLabel,
    title,
    description,
    buttonLink,
    imageUrl,
    openInNewTab = false,
    elementId,
    currentBillTo,
    isAuthenticated,
    session,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(espotCardStyles, extendedStyles));
    const history = useHistory();
    const link = useGetLink(buttonLink ?? { type: "Page", value: "" });
    const onClickHandler = () => {
        if (!link.url) {
            return;
        }
        // GA event
        if (elementId) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: "homepg_footer_widget",
                click_text: buttonLabel,
                destination: link.url,
                elementId,
                authentication_state: isAuthenticated ? "Authenticated" : "Guest",
                affiliation: currentBillTo?.companyName || "",
                sales_rep: (session as any)?.salesperson?.name || "",
            };
            dataLayer.push(googleAnalyticsData);
        }
        if (openInNewTab) {
            window.open(link.url, "_blank");
        } else {
            history.push(link.url);
        }
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={imageUrl} {...styles.image} altText={title} />
            </StyledWrapper>
            <StyledWrapper {...styles.dataWrapper}>
                <Typography {...styles.title}>{title}</Typography>
                {description && <Typography {...styles.description}>{parse(description, parserOptions)}</Typography>}
                {buttonLabel && buttonLink && (
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button onClick={onClickHandler} aria-label={buttonLabel} id={elementId}>
                            {buttonLabel}
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(EspotCard);
